import { useState } from "react";
import { toast } from "sonner";
import { X, CalendarClock } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { createSchedule } from "@/lib/api";
import { localInputToIso } from "@/lib/time";

const defaultDateTime = () => {
  const d = new Date(Date.now() + 60 * 1000);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const ScheduleFromTemplateDialog = ({ template, onClose, onScheduled }) => {
  const { t } = useI18n();
  const [when, setWhen] = useState(defaultDateTime());
  const [busy, setBusy] = useState(false);

  if (!template) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const iso = localInputToIso(when);
    if (!iso) {
      toast.error(t("toast.invalidDate"));
      return;
    }
    try {
      setBusy(true);
      await createSchedule({
        message: template.message,
        scheduled_at: iso,
        sound_enabled: template.alarm_tone !== "off",
        display_mode: template.display_mode || "fit",
        reminder_minutes: template.reminder_minutes || null,
        repeat: template.repeat || "none",
        text_size: template.text_size || "md",
        bg_colors: template.bg_colors || null,
        alarm_tone: template.alarm_tone || "off",
      });
      toast.success(t("toast.scheduled"));
      onScheduled?.();
      onClose?.();
    } catch (err) {
      toast.error(t("toast.failedSchedule"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="schedule-template-dialog"
      className="fixed inset-0 z-[9998] flex items-center justify-center p-4 bg-black/60"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="relative bg-white brutal-border brutal-shadow w-full max-w-md p-6 font-mono"
      >
        <button
          type="button"
          data-testid="schedule-template-close"
          onClick={onClose}
          className="absolute top-3 right-3 bg-white brutal-border w-9 h-9 flex items-center justify-center brutal-shadow-sm brutal-interactive"
        >
          <X className="w-5 h-5" strokeWidth={3} />
        </button>
        <h3 className="font-display text-xl uppercase mb-1 pr-10">
          {t("tplSched.title").replace("{name}", template.name)}
        </h3>
        <p className="font-mono text-xs text-neutral-600 mb-4 italic truncate">
          {template.message}
        </p>
        <label className="block font-mono text-xs uppercase tracking-widest mb-2 font-bold">
          {t("tplSched.when")}
        </label>
        <input
          data-testid="schedule-template-when"
          type="datetime-local"
          value={when}
          onChange={(e) => setWhen(e.target.value)}
          className="w-full bg-white brutal-border p-3 text-base font-mono focus:outline-none focus:bg-[#FFF9B0] transition-colors"
        />
        <button
          type="submit"
          data-testid="schedule-template-submit"
          disabled={busy}
          className="mt-4 w-full bg-[#FF3311] text-white brutal-border px-4 py-3 font-display uppercase text-base brutal-shadow brutal-interactive disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <CalendarClock className="w-4 h-4" strokeWidth={3} />
          {busy ? t("tplSched.scheduling") : t("tplSched.cta")}
        </button>
      </form>
    </div>
  );
};

export default ScheduleFromTemplateDialog;
